import { Button, ButtonSave } from "components/buttons";
import { FormSelect } from "components/form-fields";
import { FormDragAndDropFileUploader } from "components/form-fields/FormDragAndDropFileUploader";
import { ShowHide } from "components/layout";
import { Modal, WizardModal } from "components/notifications";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { DocumentsService } from "services/DocumentsService";

import './DocumentUploadModal.css';
import { DOCUMENT_ALLOWED_FILE_TYPES, DOCUMENT_MAX_FILE_SIZE_BYTES } from "pages/documents/shared/DocumentConfiguration";

export function DocumentsUploadModal({ isOpen, onClose, onUploadComplete, entityId, entityType, prefilledDocumentTypeId = null, title = null, selectedDocumentTypeName = null }) {
    const [documentTypes, setDocumentTypes] = useState([]);
    const [selectedDocumentType, setSelectedDocumentType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [wizardStep, setWizardStep] = useState(0);
    const [filesToUpload, setFilesToUpload] = useState([]);

    useEffect(() => {
        if (prefilledDocumentTypeId) {
            setSelectedDocumentType(prefilledDocumentTypeId)
            setWizardStep(1);
        }
    }, [prefilledDocumentTypeId]);

    const deleteFileFromUploadList = (fileName) => {
        setFilesToUpload((prevFiles) => prevFiles.filter(f => f.file.name !== fileName));
    }

    useEffect(() => {
        const filesWithoutVersion = filesToUpload.filter(f => !f.version);
        filesWithoutVersion.forEach((fileData) => {
            getVersionOfFile(fileData.file.name).then((version) => {
                setFilesToUpload((prevFiles) => prevFiles.map(f => {
                    if (f.file.name === fileData.file.name) {
                        return {
                            ...f,
                            version: version,
                            loadingVersion: false
                        };
                    }
                    return f;
                }));
            });
        });
    }, [filesToUpload]);

    useEffect(() => {
        if (!filesToUpload.length) {
            return;
        }

        const allFilesUploaded = filesToUpload.every(f => f.uploaded);

        if (allFilesUploaded) {
            const anyErrors = filesToUpload.some(f => f.errored);
            setTimeout(() => onUploadComplete(anyErrors));
        }
    }, [filesToUpload]);

    const isValid = useMemo(() => {
        if (wizardStep === 0) {
            return !!selectedDocumentType;
        }

        return filesToUpload.length > 0 && filesToUpload.every(f => !f.loadingVersion);
    }, [wizardStep, selectedDocumentType, filesToUpload]);

    const onWizardChange = (step, direction, previousStep) => {
        setWizardStep(step);
    }

    const uploadDocuments = () => {
        const promises = [];

        setIsSaving(true);
        filesToUpload.forEach(async (fileData) => {
            promises.push(DocumentsService.uploadDocument(fileData.file, entityId, selectedDocumentType).then(() => {
                setFileAsUploaded(fileData.file.name, false);
            }, () => {
                setFileAsUploaded(fileData.file.name, true);
            }));
        });

        return Promise.all(promises);
    }

    const setFileAsUploaded = (fileName, errored) => {
        setFilesToUpload((prevFiles) => prevFiles.map(f => {
            if (f.file.name === fileName) {
                return {
                    ...f,
                    uploaded: true,
                    errored: errored
                };
            }
            return f;
        }));
    }

    const onFilesSelected = (files) => {
        for (let file of files) {
            const fileData = {
                file: file,
                loadingVersion: true
            };

            setFilesToUpload((prevFiles) => [...prevFiles, fileData]);
        }
    }

    const getVersionOfFile = async (fileName) => {
        const searchObject = {
            filters: [
                { key: "FileName", value: fileName },
                { key: "Type", value: selectedDocumentType.toString() }
            ],
            pageNumber: 1,
            pageSize: 1,
            sortField: "Version",
            sortDirection: "D",
            entityFilter: entityId ? {
                entityId: entityId,
                entityType: entityType
            } : null
        };

        const response = await DocumentsService.getDocuments(searchObject);
        if (response.totalCount > 0) {
            return response.data[0].version + 1;
        } else {
            return 1;
        }
    }

    useEffect(() => {
        const filters = [
            { key: "Area", value: entityType },
            { key: "IgnoreDeletedTypes", value: "true" }
        ];

        const searchObject = {
            filters: filters,
            pageNumber: 1,
            pageSize: 1000, // We're note expecting more than 1000 document types
            sortField: "Name",
            sortDirection: "A"
        };

        setLoading(true);

        DocumentsService.getDocumentTypes(searchObject).then((data) => {
            const newDocumentTypes = data.data.filter(dt => !dt.featureId);
            setDocumentTypes(newDocumentTypes);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, [entityType]);

    useEffect(() => {
        if (!prefilledDocumentTypeId) {
            if (selectedDocumentTypeName) {
                const matchingDocumentType = documentTypes.find(dt => dt.name === selectedDocumentTypeName);
                if (matchingDocumentType) {
                    setSelectedDocumentType(matchingDocumentType.id);
                }
            } else {
                setSelectedDocumentType(null);
            }
            setWizardStep(0);
        }
        setFilesToUpload([]);
        setIsSaving(false);
    }, [isOpen, documentTypes]);

    const renderUploadStep = (hideHeader) => {
        return <div>
            <FormDragAndDropFileUploader
                onChange={(files) => onFilesSelected(files)}
                disabled={loading || isSaving}
                smaller={!!filesToUpload.length}
                headingText={hideHeader ? "" : "Upload Files"}
                documents={filesToUpload.map(d => ({
                    name: d.file.name,
                    size: d.file.size,
                    loadingVersion: d.loadingVersion,
                    version: d.version
                }))}
                onRemoveDocument={deleteFileFromUploadList}
                maxFileSize={DOCUMENT_MAX_FILE_SIZE_BYTES}
                allowedFileTypes={DOCUMENT_ALLOWED_FILE_TYPES}
            />
        </div>
    }

    return <ShowHide
        evaluator={!prefilledDocumentTypeId}
        show={<WizardModal
            id="new-document-upload-wizard"
            isOpen={isOpen}
            title={title ?? "Upload Documents"}
            onStepChange={onWizardChange}
            onFinish={uploadDocuments}
            canProceed={isValid}
            disabled={isSaving}
            onCancel={onClose}
            isDirty={!!selectedDocumentType}
            allowBack={true}
            canFinish={true}>
            <div className="document-upload-modal-body">
                <FormSelect
                    items={documentTypes}
                    loading={loading}
                    placeholder="Please select"
                    headingText="Please select a document type:"
                    onItemSelect={(e) => setSelectedDocumentType(e.id)}
                    selectedValue={selectedDocumentType}
                    dangerHelperText={!selectedDocumentType ? "Document type is required" : null}
                ></FormSelect>
            </div>
            {renderUploadStep(false)}
        </WizardModal>}
        hide={<Modal
            isOpen={isOpen}
            showCloseButton={false}
            clickOutsideClose={false}
            title={title ?? "Upload Documents"}
        >
            {renderUploadStep(true)}
            <div className="bp6-dialog-footer">
                <div className="bp6-dialog-footer-actions">
                    <ButtonSave text="Save" onClick={uploadDocuments} />
                    <Button text="Cancel" onClick={onClose} />
                </div>
            </div>
        </Modal>}
    />;

}

DocumentsUploadModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onUploadComplete: PropTypes.func.isRequired,
    entityId: PropTypes.number,
    entityType: PropTypes.string.isRequired,
    title: PropTypes.string,
    selectedDocumentTypeName: PropTypes.string
};